import React, { useEffect, useState, useCallback, useRef } from 'react';
import Hamburger from '../buttons/Hamburger';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavigateButton from '../buttons/NavigateButton';

function BottomDetection() {
  const location = useLocation();
  const navigate = useNavigate();
  const [receivedState, setReceivedState] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const { state } = useLocation();
  const params = useParams();
  const effectiveUserId = state?.userId ?? (params?.userId ? parseInt(params.userId, 10) : null);
  const canvasRef = useRef(null);
  const [redPoints, setRedPoints] = useState([]);
  const [drawingMode, setDrawingMode] = useState(false);
  const [errorScore, setErrorScore] = useState(null);
  const [isExampleOpen, setIsExampleOpen] = useState(false);
  const totalRedPoints = 5; // 必要な赤い点の数



  // ルート遷移時に渡された state を受け取る
  useEffect(() => {
    if (location && location.state) {
      setReceivedState(location.state);
    }
  }, [location]);

  // 画像をキャンバスに描画
  const drawImageAndPoints = useCallback(() => {
    if (receivedState && receivedState.imageUrl && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // 赤い点を描画（わかりやすく大きく）
        if (redPoints && redPoints.length > 0) {
          redPoints.forEach((point, index) => {
            // 外側の白い縁取り
            ctx.fillStyle = 'white';
            ctx.beginPath();
            ctx.arc(point.x, point.y, 16, 0, 2 * Math.PI);
            ctx.fill();
            
            // 赤い点
            ctx.fillStyle = '#FF0000';
            ctx.beginPath();
            ctx.arc(point.x, point.y, 12, 0, 2 * Math.PI);
            ctx.fill();
            
            // 中心の白い十字線
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(point.x - 8, point.y);
            ctx.lineTo(point.x + 8, point.y);
            ctx.moveTo(point.x, point.y - 8);
            ctx.lineTo(point.x, point.y + 8);
            ctx.stroke();
            
            // 番号を表示
            ctx.fillStyle = 'white';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(index + 1), point.x, point.y);
          });
        }
      };
      
      img.src = receivedState.imageUrl;
    }
  }, [receivedState, redPoints]);

  // 画像とキャンバスを準備して赤い点を描画
  useEffect(() => {
    drawImageAndPoints();
  }, [drawImageAndPoints]);

  // キャンバスクリック時に赤い点を追加
  const handleCanvasClick = (e) => {
    if (!drawingMode) return;
    if (redPoints.length >= totalRedPoints) {
      alert(`赤い点は${totalRedPoints}つまでしか追加できません`);
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // キャンバスのスケールを考慮した座標計算
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    setRedPoints([...redPoints, { x, y }]);
  };

  // 赤い点をクリア
  const clearPoints = () => {
    setRedPoints([]);
  };

  // 描画モードの切り替え
  const toggleDrawingMode = () => {
    setDrawingMode(!drawingMode);
  };

  // dataURL -> Blob 変換のユーティリティ
  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleStart = async () => {
    if (!receivedState || !receivedState.imageUrl) {
      alert('画像が見つかりません');
      return;
    }
    if (redPoints.length !== totalRedPoints) {
      alert(`赤い点を${totalRedPoints}つ追加してください`);
      return;
    }
    setIsRunning(true);
    try {
      // バックエンドに送信するデータを準備
      const canvas = canvasRef.current;
      const annotatedImage = canvas.toDataURL('image/png'); // 赤い点が描画された画像
      const originalImage = receivedState.imageUrl; // 元の画像
      const pointsData = redPoints; // 点の座標データ
      
      console.log('送信準備完了:');
      console.log('- 赤い点入り画像サイズ:', annotatedImage.length);
      console.log('- 元画像サイズ:', originalImage.length);
      console.log('- 点の座標:', pointsData);
      console.log('- userId:', effectiveUserId);
      
      // FormDataを使ってバックエンドに送信
      const formData = new FormData();
      formData.append('file', dataURLToBlob(annotatedImage), 'annotated.png'); // 赤い点入り画像
      formData.append('original_image', dataURLToBlob(originalImage), 'original.png'); // 元の画像
      formData.append('points', JSON.stringify(pointsData)); // 点の座標
      formData.append('ellipses', JSON.stringify(receivedState.ellipses || {})); // 楕円情報
      if (effectiveUserId !== null && effectiveUserId !== undefined) {
        formData.append('userId', String(effectiveUserId));
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/judgeError`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`judgeError failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('バックエンドからの応答:', data);
      
      // 誤りスコアを保存
      if (data.error_score !== undefined) {
        setErrorScore(data.error_score);
      }
      
      alert(`誤り判定処理が完了しました！\n誤りスコア: ${data.error_score}\n${data.message}`);
    } catch (err) {
      console.error('エラー:', err);
      alert('誤り判定に失敗しました: ' + (err.message || err));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="page">
      <Hamburger />
      <h1 className="heading-hero mb-2">線分抽出</h1>
      <p className="text-subtitle mb-4">楕円を抽出したので、線分を抽出します。</p>
      {receivedState && (
        <div className="card-wide">
          <div className="form-group">
            <div className="form-group">
              <p className="text-subtitle">糸底、グラスの底に対応する赤い点を{totalRedPoints}つ、画像上に追加してください。</p>
              <p className="text-subtitle">点はクリックで追加、クリアボタンで全削除できます。</p>
              <p className="text-subtitle">点を追加したら「特徴量抽出を終了」ボタンを押してください。</p>
            </div>
            <div>
              <h2 className="heading-lg">受け取ったデータ</h2>
              <p className="text-subtitle">選択数: {(() => {
                const all = receivedState.ellipses || {};
                return Object.entries(all).filter(([, v]) => Number(v.isChecked || 0) === 1).length;
              })()}</p>
            </div>
            {receivedState.imageUrl && (
              <div className="w-full flex flex-col md:flex-row md:items-start md:gap-4">
                <div className="w-full md:w-3/4 form-group items-center">
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    className="w-full rounded shadow"
                    style={{ cursor: drawingMode ? 'crosshair' : 'default' }}
                  />
                </div>
                <div className="w-full md:w-1/4 form-group">
                  <button
                    onClick={toggleDrawingMode}
                    className={`btn btn--full ${drawingMode ? 'btn--danger' : 'btn--primary'}`}
                  >
                    {drawingMode ? '描画モードオフ' : '特徴点を手動追加'}
                  </button>
                  <button
                    onClick={() => setIsExampleOpen(true)}
                    className="btn btn--gray btn--full"
                  >
                    点のセット例
                  </button>
                  <button
                    onClick={clearPoints}
                    className="btn btn--warning btn--full"
                  >
                    点をクリア ({redPoints.length}/{totalRedPoints})
                  </button>
                  <button
                    onClick={handleStart}
                    className="btn btn--secondary btn--full"
                    disabled={isRunning}
                  >
                    {isRunning ? '送信中...' : '特徴点を送信して誤り判定を開始'}
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="btn btn--gray btn--full"
                  >
                    戻る
                  </button>
                  
                </div>
              </div>
            )}
            {errorScore !== null && (
              <div className="panel bg-blue-50 border-2 border-blue-500">
                <h3 className="heading-lg text-blue-700">誤り判定結果</h3>
                <p className="heading-hero text-blue-900 text-4xl">誤りスコア: {errorScore}</p>
                <NavigateButton to="/viewer" buttonText="二次元画像との比較画面へ戻る" state={{ userId: effectiveUserId, errorScore }} />
              </div>
            )}
          </div>
        </div>
      )}
      {isExampleOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card">
            <div className="modal-header">
              <h2 className="heading-lg">点のセット例</h2>
              <button
                onClick={() => setIsExampleOpen(false)}
                className="btn btn--gray"
              >
               ×
              </button>
            </div>
            <div className="modal-body">
              <img
                src="/setPointEx.png"
                alt="点のセット例"
                className="modal-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BottomDetection;